import React, { forwardRef } from 'react'
import styled from 'styled-components'

import { useResolveSizeLayout } from '../../../hooks/blockStyle/size'
import { flexMinSizeStyle, getPositionStyle, getSizeStyle } from '../../../utils/layout'
import type { VisibleDomProps } from '../../VisibleDom'
import { useContainerLayoutContext, useFlowLayoutContext } from '../Context'
import type { FlowLayoutNode, NodeRenderProps } from '../types'

type BoxProps = {
    disabled?: boolean
}

const StyledBox = styled.div<BoxProps>`
    display: flex;
    flex-direction: column;
    position: relative;
    flex-shrink: 0;

    &[data-highlight='true'] {
        background-color: #5551ff1f;
        outline: 2px solid var(--color-main);
        outline-offset: -1px;
    }
`

export interface FlowLayoutContainerNodeProps extends React.ComponentPropsWithoutRef<'div'> {
    data: FlowLayoutNode
    disabled?: boolean
    extra?: React.ReactNode
    onDataDrawerVisible?: (val: boolean) => void
    nodeRender?: (props: NodeRenderProps) => JSX.Element | null
}

export const ContainerNode = forwardRef<HTMLDivElement, FlowLayoutContainerNodeProps>((props, ref) => {
    const { data, style, disabled: propDisabled, onDataDrawerVisible, nodeRender, children, extra, ...rest } = props
    const { size, position } = data.data || {}

    const { highlight, nodeRender: globalNodeRender = React.Fragment } = useFlowLayoutContext()
    const NodeRender = nodeRender ?? globalNodeRender
    const { sortable } = useFlowLayoutContext()
    const parentLayoutCtx = useContainerLayoutContext()
    const resolvedSize = useResolveSizeLayout(size)

    return (
        <StyledBox
            ref={ref}
            data-type="container"
            data-node-id={data.id}
            data-highlight={!!highlight?.[data.id]?.self}
            style={{
                ...flexMinSizeStyle,
                ...getSizeStyle(resolvedSize, parentLayoutCtx.layout?.align?.direction, sortable),
                ...style,
                ...getPositionStyle(position, parentLayoutCtx.level)
            }}
            {...rest}
        >
            <NodeRender node={data} disabled={propDisabled}>
                {children}
                {extra}
            </NodeRender>
        </StyledBox>
    )
})
